import {
    Box,
    Icon,
    Input,
    InputGroup,
    Menu,
    Portal,
    Stack,
    Text,
    useMenuContext,
    VisuallyHidden,
} from '@chakra-ui/react';
import type React from 'react';
import { Fragment, useEffect, useState } from 'react';
import { LuCheck, LuSearch } from 'react-icons/lu';
import { AccountAvatar } from './account-avatar';

// App-agnostic account shape: the app builds these from its own session
// data.
export type SwitcherAccount = {
    id: string;
    name?: string | null;
    logoUrl?: string | null;
};

/** A plain command row (e.g. "Add organization"); picks report through
 * `onAction`. */
export type SwitcherAction = {
    value: string;
    label: string;
    icon?: React.ElementType;
};

/** A run of the menu: account rows (optionally behind a filter box),
 * command rows, or both. Rendered bare or as a labelled Chakra item group. */
export type SwitcherSection = {
    label?: string;
    accounts?: SwitcherAccount[];
    /** The lead context's section (e.g. the hub): its row wears the larger
        tile. */
    emphasis?: boolean;
    /** A search box above the rows, filtering them by display name as
        typed; the value is its placeholder. */
    search?: string;
    /** The disabled row shown when no account row remains (default
        "None"). */
    emptyLabel?: string;
    /** Command rows after the account rows. */
    actions?: SwitcherAction[];
    /** A rule under this section. */
    divider?: boolean;
};

// 320px floor (the previous switcher's width) that yields to a narrow
// viewport (Chakra's popover has this clamp; its menu doesn't).
const MENU_MIN_W = 'min(calc(100dvw - 1rem), {sizes.xs})';

export type AccountSwitcherMenuProps = {
    sections: SwitcherSection[];
    /** Fires on an action pick (the menu closes itself). */
    onAction?: (value: string) => void;
    /** The current context's row: check-marked. */
    selectedId?: string;
    /** Fires on any row pick (the menu closes itself). */
    onSelect?: (account: SwitcherAccount) => void;
    /** The trigger, rendered via Menu.Trigger asChild — a single element
        forwarding props/ref (the context row's Sidebar.ContextTrigger). */
    trigger: React.ReactNode;
    /** Menu placement — defaults to below the trigger's start edge. */
    positioning?: Menu.RootProps['positioning'];
};

/** The context switcher: a Chakra Menu of account rows. The app owns the
 * data and the switch semantics (onSelect); this owns the visuals. */
export function AccountSwitcherMenu({
    sections,
    onAction,
    selectedId,
    onSelect,
    trigger,
    positioning,
}: AccountSwitcherMenuProps) {
    const byId = new Map(
        sections.flatMap((section) =>
            (section.accounts ?? []).map(
                (account) => [account.id, account] as const,
            ),
        ),
    );
    const actionValues = new Set(
        sections.flatMap((section) =>
            (section.actions ?? []).map((action) => action.value),
        ),
    );
    const pick = ({ value }: { value: string }) => {
        const account = byId.get(value);
        if (account) {
            onSelect?.(account);
            return;
        }
        if (actionValues.has(value)) onAction?.(value);
    };
    return (
        <Menu.Root
            // Defer the list's subtree until first open: the menu rides
            // every page's chrome but most sessions never open it.
            lazyMount
            // Letters typed with focus on the list itself must not jump the
            // highlight — the search box is where typing goes.
            typeahead={false}
            positioning={{ placement: 'bottom-start', ...positioning }}
            onSelect={pick}
        >
            <Menu.Trigger asChild>{trigger}</Menu.Trigger>
            <Portal>
                <Menu.Positioner>
                    <Menu.Content minW={MENU_MIN_W}>
                        {sections.map((section, index) => (
                            // Static section lists at every call site —
                            // position is identity.
                            <Fragment key={index}>
                                <Section
                                    index={index}
                                    section={section}
                                    selectedId={selectedId}
                                />
                                {section.divider && <Menu.Separator />}
                            </Fragment>
                        ))}
                    </Menu.Content>
                </Menu.Positioner>
            </Portal>
        </Menu.Root>
    );
}

function Section({
    index,
    section,
    selectedId,
}: {
    index: number;
    section: SwitcherSection;
    selectedId?: string;
}) {
    const [query, setQuery] = useState('');
    const needle = query.trim().toLowerCase();
    // On the row's label only: ids are reverse-DNS, so matching them would
    // light every row up for "com" or "ionq".
    const accounts = (section.accounts ?? []).filter(
        (account) =>
            !needle || rowLabel(account).toLowerCase().includes(needle),
    );
    // Typing highlights the first remaining row, so Enter picks it (Zag
    // acts on the highlighted item; a filtered-out highlight would leave
    // Enter dead).
    const menu = useMenuContext();
    const emptyValue = `section-${index}:none`;
    // With a filter on: the first remaining row, or the (disabled) empty-
    // state row when nothing remains — so aria-activedescendant never
    // names a row that left the DOM, and Enter has nothing live to pick.
    // Clearing the filter moves a highlight parked on the empty-state row
    // back to the first real row (else Enter would stay dead).
    const parkedOnEmpty = menu.highlightedValue === emptyValue;
    const firstId = needle
        ? (accounts[0]?.id ?? emptyValue)
        : parkedOnEmpty
          ? (accounts[0]?.id ?? null)
          : null;
    const highlightedVisible = accounts.some(
        (account) => account.id === menu.highlightedValue,
    );
    useEffect(() => {
        if (firstId && !highlightedVisible) menu.setHighlightedValue(firstId);
    }, [firstId, highlightedVisible, menu.setHighlightedValue]);
    const body = (
        <>
            {section.search !== undefined && (
                <>
                    <SearchBox
                        placeholder={section.search}
                        value={query}
                        onChange={setQuery}
                    />
                    {/* The filter's outcome, spoken: rows entering and
                        leaving a menu are silent otherwise. */}
                    <VisuallyHidden role='status'>
                        {needle &&
                            (accounts.length === 0
                                ? (section.emptyLabel ?? 'None')
                                : `${accounts.length} results`)}
                    </VisuallyHidden>
                </>
            )}
            {section.accounts && accounts.length === 0 && (
                <Menu.Item
                    value={emptyValue}
                    disabled
                    // Highlighted for AT (aria-activedescendant names it when
                    // a filter clears the list) but painted flat: a lit
                    // "nothing here" row would read as a pick.
                    css={{ '&[data-highlighted]': { bg: 'transparent' } }}
                >
                    {section.emptyLabel ?? 'None'}
                </Menu.Item>
            )}
            {accounts.map((account) => (
                <AccountRow
                    key={account.id}
                    account={account}
                    selected={account.id === selectedId}
                    emphasis={section.emphasis}
                />
            ))}
            {(section.actions ?? []).map((action) => (
                <Menu.Item key={action.value} value={action.value}>
                    {action.icon && (
                        <Icon
                            as={action.icon}
                            aria-hidden
                            boxSize={4}
                            color='fg.muted'
                        />
                    )}
                    {action.label}
                </Menu.Item>
            ))}
        </>
    );
    if (section.label) {
        return (
            <Menu.ItemGroup>
                {/* A quiet caption over the rows: regular weight, muted ink
                    (same in both apps and modes). */}
                <Menu.ItemGroupLabel fontWeight='normal' color='fg.muted'>
                    {section.label}
                </Menu.ItemGroupLabel>
                {body}
            </Menu.ItemGroup>
        );
    }
    return body;
}

// Keys the menu itself handles: closing, moving the highlight through the
// rows and picking the highlighted one. Everything else stays in the box
// (Zag's content handler would otherwise treat Home/End/Space as list
// navigation). Tab is handled here: the box is the menu's only tabbable,
// so Zag's tab guard would swallow it — close instead (focus returns to
// the trigger).
const MENU_KEYS = new Set(['Escape', 'Enter', 'ArrowDown', 'ArrowUp']);

function SearchBox({
    placeholder,
    value,
    onChange,
}: {
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
}) {
    const menu = useMenuContext();
    const { open } = menu;
    // The box drives the list the way a combobox does: the highlighted row
    // is announced through aria-activedescendant on the FOCUSED element
    // (Zag sets it on the menu content, which never has focus here).
    const activeId = menu.highlightedValue
        ? menu.getItemState({ value: menu.highlightedValue }).id
        : undefined;
    // Zag's open focus lands on the box itself (the menu's first tabbable);
    // a fresh filter every time it closes.
    useEffect(() => {
        if (!open) onChange('');
    }, [open, onChange]);
    return (
        <Box
            px={1}
            pt={1}
            pb={2}
            // Pinned while a long list scrolls under it.
            position='sticky'
            top={0}
            zIndex={1}
            bg='bg.panel'
        >
            <InputGroup
                startElement={<Icon as={LuSearch} aria-hidden boxSize={4} />}
            >
                <Input
                    size='sm'
                    variant='outline'
                    role='combobox'
                    // The placeholder minus its ellipsis (spoken as dots).
                    aria-label={placeholder.replace(/(\.{3}|…)$/, '')}
                    aria-expanded={open}
                    aria-autocomplete='list'
                    aria-controls={menu.getContentProps().id}
                    aria-activedescendant={activeId}
                    placeholder={placeholder}
                    autoComplete='off'
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Tab') {
                            event.preventDefault();
                            event.stopPropagation();
                            menu.setOpen(false);
                            return;
                        }
                        if (!MENU_KEYS.has(event.key)) event.stopPropagation();
                    }}
                />
            </InputGroup>
        </Box>
    );
}

const rowLabel = (account: SwitcherAccount) => account.name || account.id;

function AccountRow({
    account,
    selected,
    emphasis,
}: {
    account: SwitcherAccount;
    selected: boolean;
    emphasis?: boolean;
}) {
    const label = rowLabel(account);
    return (
        <Menu.Item
            value={account.id}
            // Two-line rows: the item's default single-line height is
            // released; the tile sets the height.
            h='auto'
            py='2'
            gap='3'
            // The current context: a trailing check. The icon alone is
            // visual — aria-current carries the state.
            aria-current={selected ? 'true' : undefined}
        >
            {/* Standard sizes: md (40px) for the lead context, xs (32px)
                rows — the initials scale with the tile. */}
            <AccountAvatar
                name={label}
                logoUrl={account.logoUrl}
                size={emphasis ? 'md' : 'xs'}
            />
            {/* Name above the mono id (1.2 line boxes so truncate doesn't
                shave descenders). Org ids are reverse-DNS (no hyphens) —
                there is no "short" form, so the id renders whole but
                truncated, and only when a name labels the row. */}
            <Stack flex='1' minW={0} gap={0.5}>
                <Text data-sentry-mask fontSize='sm' lineHeight={1.2} truncate>
                    {label}
                </Text>
                {account.name && (
                    <Text
                        data-sentry-mask
                        fontSize='2xs'
                        fontFamily='mono'
                        color='fg.muted'
                        lineHeight={1.2}
                        truncate
                    >
                        {account.id}
                    </Text>
                )}
            </Stack>
            {selected && <Icon as={LuCheck} aria-hidden boxSize={4} />}
        </Menu.Item>
    );
}
